// services/gameService.js
import axios from "axios";
import { refreshAccessToken } from "./authSession";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

export const getAuthHeaders = () => {
  const token = localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getGameId = async (gameRoutePath) => {
  try {
    const url = `${SERVER_URL}/api/get-game-data/${gameRoutePath}/`;
    const response = await axios.get(url);
    return response.data.game_id;
  } catch (error) {
    console.error("Error retrieving game data:", error);
    return null;
  }
};

export const getLevelsByGame = async (gameId) => {
  try {
    let response;

    response = await axios.get(`${SERVER_URL}/api/levels-by-game/`, {
      params: { game_id: gameId },
    });
    const levels = response.data;
    return levels;
  } catch (error) {
    console.error("Error fetching levels by game parameters:", error);
    return null;
  }
};

export const getLevel1ByGame = async (gameId) => {
  const levels = await getLevelsByGame(gameId);
  if (levels) {
    return levels[0];
  } else {
    return null;
  }
};

export const getSessionDetailsFromSessionId = async (sessionId) => {
  try {
    // Auth-required since #29. Keep the trailing slash: without it Django
    // 301-redirects and the browser drops the Authorization header, so the
    // post-game level promotion in endSession silently never ran.
    const response = await axios.get(
      `${SERVER_URL}/api/session-details/${sessionId}/`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getCurrentUser = () => {
  var user = null;
  const userString = localStorage.getItem("user");
  if (userString) {
    // Check if the userString is a double string
    const parsedUser = userString.startsWith('"')
      ? JSON.parse(userString)
      : userString;
    user = JSON.parse(parsedUser); // Parse once to get the object
  }
  return user;
};

export const getCurrentGameLevelForUser = async (gameId) => {
  try {
    const response = await axios.get(
      `${SERVER_URL}/api/current-game-level-for-user/`,
      {
        params: {
          game_id: gameId,
        },
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const setCurrentGameLevelForUser = async (gameId, levelNumber) => {
  try {
    const response = await axios.put(
      `${SERVER_URL}/api/current-game-level-for-user/`,
      {
        game_id: gameId,
        level_number: levelNumber,
      },
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const startSession = async (gameRoutePath) => {
  try {
    var userId = null;
    const user = getCurrentUser();
    if (user) {
      userId = user.id; // Accessing user id
    }

    let level = null;
    const gameId = await getGameId(gameRoutePath);

    if (!gameId) {
      throw new Error("Game ID could not be retrieved");
    }

    if (userId) {
      level = await getCurrentGameLevelForUser(gameId);
    } else {
      level = await getLevel1ByGame(gameId);
      return { sessionId: null, level: level };
    }

    const response = await axios.post(
      `${SERVER_URL}/api/start-session/`,
      {
        game_id: gameId,
        // Stamp the (freemium-capped) level actually being played so
        // per-level durations can be reported later.
        level_id: level ? level.id : null,
      },
      {
        headers: getAuthHeaders(),
      },
    );
    const sessionId = response.data.session_id;
    return { sessionId: sessionId, level: level };
  } catch (error) {
    console.error("Error starting game session:", error);
  }
};

// `activeMs` is the play time the game measured (see usePlayClock); it's sent
// as null when the game doesn't measure it.
export const endSession = async (sessionId, score, activeMs = null) => {
  try {
    if (!sessionId) {
      console.error("Session was not initiated");
      return null;
    }

    // Extract just the sessionId string if it's an object
    const actualSessionId =
      typeof sessionId === "object" ? sessionId.sessionId : sessionId;

    console.log(
      "Ending session with ID:",
      actualSessionId,
      "and score:",
      score,
    );

    const response = await axios.post(
      `${SERVER_URL}/api/end-session/`,
      {
        session_id: actualSessionId,
        score: score,
        completed: true,
        active_ms: Number.isFinite(activeMs) ? activeMs : null,
      },
      {
        headers: getAuthHeaders(),
      },
    );

    console.log("End session response:", response.data);

    // Check if user should move to the next (or previous) level
    const session = await getSessionDetailsFromSessionId(actualSessionId);
    if (session && session.user && session.game) {
      const sessionScore = session.game_score;
      const levelResponse = await getCurrentGameLevelForUser(session.game);

      if (levelResponse && levelResponse.level_number != null) {
        const userLevelNumber = levelResponse.level_number;

        if (
          sessionScore <= levelResponse.level_min_score_threshold
        ) {
          // Demote if score is below minimum threshold
          if (userLevelNumber > 1) {
            await setCurrentGameLevelForUser(
              session.game,
              userLevelNumber - 1,
            );
          }
        } else if (
          sessionScore >= levelResponse.level_max_score_threshold &&
          levelResponse.level_max_score_threshold > 0
        ) {
          // Promote if score meets or exceeds max threshold
          const gameLevels = await getLevelsByGame(session.game);
          if (gameLevels && gameLevels.length > userLevelNumber) {
            await setCurrentGameLevelForUser(
              session.game,
              userLevelNumber + 1,
            );
          }
        }
      }
    }

    // Return session data
    if (response.data && response.data.game_score !== undefined) {
      return response.data;
    } else {
      console.warn(
        "Session ended but no game_score in response:",
        response.data,
      );
      return {
        session_id: actualSessionId,
        game_score: score,
        completed: true,
        finished_at: new Date().toISOString(),
      };
    }
  } catch (error) {
    console.error("Error ending game session:", error);

    // Return a fallback object to prevent undefined errors
    const actualSessionId =
      typeof sessionId === "object" ? sessionId.sessionId : sessionId;
    return {
      session_id: actualSessionId,
      game_score: score || 0,
      completed: false,
      error: true,
    };
  }
};
export const recordKeystrokes = async (sessionId, keystrokes) => {
  try {
    if (!sessionId) {
      console.error("Session was not initiated");
      return null;
    }
    const response = await axios.post(
      `${SERVER_URL}/api/record-keystrokes/`,
      {
        session_id: sessionId,
        keystrokes: keystrokes,
      },
      { headers: getAuthHeaders() },
    );
    return response.data;
  } catch (error) {
    console.error("Error storing session keystrokes:", error);
    throw error;
  }
};

export const recordMemoryMatrixMouseClicks = async (sessionId, clicks) => {
  try {
    if (!sessionId) {
      console.error("Session was not initiated");
      return null;
    }
    const response = await axios.post(
      `${SERVER_URL}/api/record-memory-matrix-mouse-clicks/`,
      {
        session_id: sessionId,
        mouse_clicks: clicks,
      },
      { headers: getAuthHeaders() },
    );
    return response.data;
  } catch (error) {
    console.error("Error storing session clicks:", error);
    throw error;
  }
};

// --- Patient management (clinician / Admin Mode) ---
// These hit the clinician-scoped /api/patients/ endpoints and surface errors
// to the caller so forms can show validation messages.

export const listPatients = async () => {
  const response = await axios.get(`${SERVER_URL}/api/patients/`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getPatient = async (id) => {
  const response = await axios.get(`${SERVER_URL}/api/patients/${id}/`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createPatient = async (data) => {
  const response = await axios.post(`${SERVER_URL}/api/patients/`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updatePatient = async (id, data) => {
  const response = await axios.patch(
    `${SERVER_URL}/api/patients/${id}/`,
    data,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

export const deactivatePatient = async (id) => {
  await axios.post(`${SERVER_URL}/api/patients/${id}/deactivate/`, null, {
    headers: getAuthHeaders(),
  });
};

export const getGames = async () => {
  const response = await axios.get(`${SERVER_URL}/api/games/`);
  return response.data;
};

// The signed-in user's own game stats (Patient Mode own-stats view).
export const getMyStats = async () => {
  const response = await axios.get(`${SERVER_URL}/api/my-stats/`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Per-game comparison of the user against peers in their age band.
export const getMyStatsComparison = async () => {
  const response = await axios.get(`${SERVER_URL}/api/my-stats/comparison/`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Birth year is optional at sign-up; the age comparison needs it.
export const setMyBirthYear = async (year) => {
  const response = await axios.patch(
    `${SERVER_URL}/api/auth/users/me/`,
    { date_of_birth: `${year}-01-01` },
    { headers: getAuthHeaders() },
  );
  return response.data;
};

// --- Stats share links (read-only links a patient sends to a clinician) ---

// List the user's active share links.
export const getStatsShareLinks = async () => {
  const response = await axios.get(`${SERVER_URL}/api/my-stats/share/`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Create a new share link (30-day expiry, returns { token, expires_at, ... }).
export const createStatsShareLink = async () => {
  const response = await axios.post(
    `${SERVER_URL}/api/my-stats/share/`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Revoke a share link by token.
export const revokeStatsShareLink = async (token) => {
  await axios.delete(`${SERVER_URL}/api/my-stats/share/${token}/`, {
    headers: getAuthHeaders(),
  });
};

// Public read-only stats for a share token (no auth — the token is the key).
export const getSharedStats = async (token) => {
  const response = await axios.get(
    `${SERVER_URL}/api/shared-stats/${token}/`
  );
  return response.data;
};

// --- Per-patient game settings (#34) ---

export const getPatientGameSettings = async (patientId) => {
  const response = await axios.get(
    `${SERVER_URL}/api/patients/${patientId}/game-settings/`,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

export const getPatientGameSettingsForGame = async (patientId, gameId) => {
  const response = await axios.get(
    `${SERVER_URL}/api/patients/${patientId}/game-settings/${gameId}/`,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

export const updatePatientGameSettings = async (patientId, gameId, settings) => {
  const response = await axios.put(
    `${SERVER_URL}/api/patients/${patientId}/game-settings/${gameId}/`,
    { settings },
    { headers: getAuthHeaders() },
  );
  return response.data;
};

// Used by game wrappers to load the signed-in patient's own config for a game.
export const getMyGameSettings = async (gameId) => {
  const response = await axios.get(
    `${SERVER_URL}/api/my-game-settings/${gameId}/`,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

// --- Subscriptions / payments ---

// Start a Stripe Checkout Session for a plan; returns { sessionId, url }.
export const createCheckoutSession = async (planId) => {
  const response = await axios.post(
    `${SERVER_URL}/api/create-checkout-session/`,
    { plan_id: planId },
    { headers: getAuthHeaders() },
  );
  return response.data;
};

// The signed-in user's subscription status (checkout return + game gating).
export const getMySubscription = async () => {
  const response = await axios.get(`${SERVER_URL}/api/my-subscription/`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Public front-end config flags (e.g. { payments_enabled }). No auth needed.
export const getPublicConfig = async () => {
  const response = await axios.get(`${SERVER_URL}/api/public-config/`);
  return response.data;
};

// Available paid plans for the pricing page.
export const getSubscriptionPlans = async () => {
  const response = await axios.get(`${SERVER_URL}/api/subscription-plans/`);
  return response.data;
};

// Open the Stripe Billing Portal to manage / cancel the subscription.
export const createBillingPortalSession = async () => {
  const response = await axios.post(
    `${SERVER_URL}/api/create-billing-portal-session/`,
    {},
    { headers: getAuthHeaders() },
  );
  return response.data;
};

// A multiple-choice round (target + options) for the image↔word games (#37).
// params: { category, options } (both optional).
export const getContentRound = async (params = {}) => {
  const response = await axios.get(`${SERVER_URL}/api/content/round/`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data;
};

// Per-patient session report (clinician).
export const getPatientSessions = async (patientId) => {
  const response = await axios.get(
    `${SERVER_URL}/api/patients/${patientId}/sessions/`,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

// Download a patient's sessions as a CSV (auth-protected, so fetch + blob).
export const downloadPatientSessionsCsv = async (patientId) => {
  const url = `${SERVER_URL}/api/patients/${patientId}/sessions/export/`;
  let response = await fetch(url, { headers: getAuthHeaders() });
  // fetch bypasses the axios interceptor, so renew an expired token here.
  if (response.status === 401 && (await refreshAccessToken())) {
    response = await fetch(url, { headers: getAuthHeaders() });
  }
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `patient_${patientId}_sessions.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// Cross-patient aggregate stats for the signed-in clinician.
// params: { game, date_from, date_to } (all optional).
export const getPatientStats = async (params = {}) => {
  const response = await axios.get(`${SERVER_URL}/api/admin/patient-stats/`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data;
};
